import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { useCopy } from '@floorfile/i18n'
import { Button, Dialog } from '@floorfile/ui'
import { componentsCopy } from './copy'

/**
 * The product's answer to `window.confirm` and `window.prompt`.
 *
 * Fifteen call sites across twelve features asked the BROWSER to confirm a
 * destructive action or to collect a reason. From the user's side that is the
 * same defect every time: it is not the product's dialog, it cannot be styled
 * or translated (Chrome writes "OK"/"Cancel" in the BROWSER's language, not
 * the app's), it cannot say what will actually happen beyond one line of plain
 * text, it blocks the whole tab, and on a phone it is a system sheet that
 * looks like a browser warning rather than part of the app. Worse for this
 * product specifically: several of those confirmations gate a regulated
 * action, and a reason typed into `prompt()` becomes part of a permanent
 * quality record.
 *
 * Why a PROVIDER with promises rather than a component each caller renders:
 * `window.confirm` is called inline in an event handler and its answer is used
 * on the next line. Every call site here has that shape. A promise-returning
 * hook is a one-line change at each of them (`if (!(await ask.confirm(…)))
 * return`), where an open/close state machine per site would be fourteen
 * rewrites and fourteen chances to get the pending/disabled/error handling
 * subtly different. The dialog itself is the shared `Dialog`, so it portals to
 * `<body>`, traps focus, and closes on Escape like every other dialog.
 *
 * If the caller unmounts while the dialog is open the promise still settles —
 * the provider owns the state, and nothing is listening, which is the same
 * outcome as a cancelled action.
 */

export interface ConfirmRequest {
  title: string
  /** What will happen, in the product's words. Optional for a self-evident action. */
  body?: ReactNode
  /** Defaults to the shared "Confirm". */
  confirmLabel?: string
  /** Defaults to the shared "Cancel". */
  cancelLabel?: string
  /** `destructive` tints the confirm button — deletes, cancellations, removals. */
  tone?: 'default' | 'destructive'
}

export interface TextRequest extends Omit<ConfirmRequest, 'tone'> {
  /** The field's label. */
  label: string
  placeholder?: string
  /** Refuse an empty answer — a reason that goes on the record is required. */
  required?: boolean
  maxLength?: number
  /** A textarea instead of a single line, for a narrative. */
  multiline?: boolean
  /** F15: the current value when the question EDITS one (a pin's label). */
  initialValue?: string
}

/**
 * Whether a typed answer may be accepted, and what it becomes.
 *
 * Pulled out of the component so it can be tested without a DOM (apps/web has
 * no render stack; `packages/ui` owns that). It is the rule every call site
 * used to write for itself as `window.prompt(...)?.trim()` followed by
 * `if (!note) return` — which SILENTLY did nothing when the person pressed OK
 * on an empty box. Here an empty required answer is refused out loud and the
 * dialog stays open.
 */
export function textAnswer(
  request: Pick<TextRequest, 'required'>,
  raw: string,
): { accepted: true; value: string } | { accepted: false } {
  const value = raw.trim()
  if (request.required === true && value === '') return { accepted: false }
  return { accepted: true, value }
}

type Pending =
  | { kind: 'confirm'; request: ConfirmRequest; settle: (value: boolean) => void }
  | { kind: 'text'; request: TextRequest; settle: (value: string | undefined) => void }

interface Ask {
  /** Resolves true when the person confirmed. */
  confirm: (request: ConfirmRequest) => Promise<boolean>
  /** Resolves the text, or `undefined` when they cancelled. */
  forText: (request: TextRequest) => Promise<string | undefined>
}

const AskContext = createContext<Ask | undefined>(undefined)

/**
 * Callers get this rather than the context directly so a component mounted
 * outside the provider fails loudly in development instead of silently
 * skipping a confirmation — which would turn a guarded delete into an
 * unguarded one.
 */
export function useAsk(): Ask {
  const ask = useContext(AskContext)
  if (!ask) throw new Error('useAsk() needs <AskProvider> — see apps/web/src/app/providers.tsx')
  return ask
}

export function AskProvider({ children }: { children: ReactNode }) {
  const c = useCopy(componentsCopy).ask
  const [pending, setPending] = useState<Pending | undefined>(undefined)
  const [text, setText] = useState('')
  const [touched, setTouched] = useState(false)
  // One question at a time: a second call while one is open would otherwise
  // drop the first caller's promise on the floor, unsettled, forever.
  const queued = useRef(false)

  const close = useCallback(() => {
    setPending(undefined)
    setText('')
    setTouched(false)
    queued.current = false
  }, [])

  const ask = useMemo<Ask>(
    () => ({
      confirm: (request) =>
        new Promise<boolean>((resolve) => {
          if (queued.current) {
            resolve(false)
            return
          }
          queued.current = true
          setPending({ kind: 'confirm', request, settle: resolve })
        }),
      forText: (request) =>
        new Promise<string | undefined>((resolve) => {
          if (queued.current) {
            resolve(undefined)
            return
          }
          queued.current = true
          setText(request.initialValue ?? '')
          setTouched(false)
          setPending({ kind: 'text', request, settle: resolve })
        }),
    }),
    [],
  )

  function cancel() {
    if (!pending) return
    // Both kinds have a "no" value, and it is the one a closed dialog means.
    if (pending.kind === 'confirm') pending.settle(false)
    else pending.settle(undefined)
    close()
  }

  function accept() {
    if (!pending) return
    if (pending.kind === 'confirm') {
      pending.settle(true)
      close()
      return
    }
    const answer = textAnswer(pending.request, text)
    if (!answer.accepted) {
      setTouched(true)
      return
    }
    pending.settle(answer.value)
    close()
  }

  const missing = pending?.kind === 'text' && pending.request.required === true && touched && text.trim() === ''

  return (
    <AskContext.Provider value={ask}>
      {children}
      {pending && (
        <Dialog
          open
          onClose={cancel}
          title={pending.request.title}
          {...(typeof pending.request.body === 'string' ? { description: pending.request.body } : {})}
          className="max-w-md"
        >
          <div className="space-y-3">
            {/* A non-string body is richer than `description` takes — a list of
                what will be cancelled, a record number in a mono span. */}
            {pending.request.body && typeof pending.request.body !== 'string' && (
              <div className="text-sm text-muted-foreground">{pending.request.body}</div>
            )}
            {pending.kind === 'text' && (
              <label className="block text-sm">
                <span className="mb-1 block text-muted-foreground">
                  {pending.request.label}
                  {pending.request.required ? ' *' : ''}
                </span>
                {pending.request.multiline ? (
                  <textarea
                    autoFocus
                    rows={3}
                    value={text}
                    maxLength={pending.request.maxLength ?? 1000}
                    placeholder={pending.request.placeholder}
                    onChange={(event) => setText(event.target.value)}
                    aria-invalid={missing || undefined}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                ) : (
                  <input
                    autoFocus
                    value={text}
                    maxLength={pending.request.maxLength ?? 500}
                    placeholder={pending.request.placeholder}
                    onChange={(event) => setText(event.target.value)}
                    // Enter submits, the way `prompt()` did.
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                        accept()
                      }
                    }}
                    aria-invalid={missing || undefined}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                )}
                {missing && (
                  <span role="alert" className="mt-1 block text-xs text-destructive">
                    {c.required}
                  </span>
                )}
              </label>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={cancel}>
                {pending.request.cancelLabel ?? c.cancel}
              </Button>
              <Button
                variant={pending.kind === 'confirm' && pending.request.tone === 'destructive' ? 'destructive' : 'primary'}
                onClick={accept}
              >
                {pending.request.confirmLabel ?? c.confirm}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </AskContext.Provider>
  )
}
